import { cn, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, useDisclosure } from '@ds';
import { Button as NextButton } from '@heroui/button';
import { BsThreeDots } from 'react-icons/bs';
import { CaseAttachmentsModal } from '@features/modals/CaseAttachmentsModal/CaseAttachmentsModal';
import { AuthContext } from '@lp/core/auth';
import { useContext } from 'react';
import { DeleteDraftModal } from '@features/components/CaseCard/Blocks/Base/CardMenu/DeleteDraftModal/DeleteDraftModal';
import { ActionHintIcon } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/ActionHintIcon';
import { CaseStatusChangeModals } from '@lp/features/CaseStatusActions/components/CaseStatusChangeModals';
import { useCaseStatusMenuState } from '@lp/features/CaseStatusActions/hooks/useCaseStatusMenuState';
import { getCaseStatusName, hidden, isAllowedStatus } from '@/utils/helpers';
import { Case, CaseStatus } from '@/api/apis/case';
import { useDeleteDraftCaseMutation } from '@/queries/case';
import { CaseBoardPermissions, CaseDetailsPermissions } from '@/permissions';
import { caseStatusNames } from '@/constants/case/case.constants';

type CardMenuProps = {
  openResources?: () => void;
  caseData: Case;
};

export const CardMenu = ({ openResources, caseData }: CardMenuProps) => {
  const { status, caseId, files, links, randomization } = caseData;

  const { checkPermission } = useContext(AuthContext);
  const canDeleteDraft = checkPermission(CaseBoardPermissions.CaseDraftDelete);
  const hasEditCaseDonorPermission = checkPermission(CaseDetailsPermissions.CaseDonorUpdate);

  const {
    hasPermission: hasUpdatePermission,
    isDirectAcquisitionCase,
    proceedHidden,
    nextStatus,
    prevStatus,
    canMoveToPrevStatus,
    canDeclineCase,
    canDeclineService,
    proceedDisabled,
    proceedInteractionDisabled,
    disabledKeys,
    actionHintMessage,
    updateStatus,
    onProceedClick,
    onOcs,
    offOcs,
    declineCase,
    declineService,
  } = useCaseStatusMenuState(caseData);

  const isDaCaseMenuAllowed =
    isDirectAcquisitionCase &&
    status &&
    isAllowedStatus({
      statusFrom: CaseStatus.Draft,
      statusTo: CaseStatus.ClinicalScreening,
      currentStatus: status,
    });

  const { isOpen: isOpenDelete, onOpenChange: onOpenChangeDelete, onOpen: onOpenDelete } = useDisclosure();
  const {
    isOpen: isOpenAttachments,
    onOpenChange: onOpenChangeAttachments,
    onOpen: onOpenAttachments,
  } = useDisclosure();

  const { mutateAsync: mutateDraftDelete } = useDeleteDraftCaseMutation();

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown placement="right-start" className="bg-base-60">
        <DropdownTrigger>
          <NextButton
            className="bg-transparent p-1 min-w-0 h-auto hover:bg-white"
            radius="full"
            disableRipple
            endContent={<BsThreeDots size={24} />}
          />
        </DropdownTrigger>

        <DropdownMenu selectionMode="none" disabledKeys={disabledKeys}>
          <DropdownItem
            key="resources"
            textValue="resources"
            className={hidden(!openResources || (isDirectAcquisitionCase && !isDaCaseMenuAllowed))}
            onClick={openResources}
          >
            Show Full Team
          </DropdownItem>

          <DropdownItem
            key="showSummary"
            textValue="showSummary"
            className={hidden(isDirectAcquisitionCase && !isDaCaseMenuAllowed)}
            onClick={onOpenAttachments}
          >
            View Attachments
          </DropdownItem>

          <DropdownItem
            key="proceedStatus"
            textValue="proceedStatus"
            className={hidden(proceedHidden || !hasUpdatePermission)}
            onClick={onProceedClick}
            endContent={
              <div className="pointer-events-auto">
                <ActionHintIcon show={proceedDisabled} message={actionHintMessage} />
              </div>
            }
          >
            <div
              className={cn({
                'opacity-50 cursor-not-allowed': proceedInteractionDisabled,
              })}
            >
              Proceed to{' '}
              {isDirectAcquisitionCase ? caseStatusNames.Closed : getCaseStatusName(nextStatus, randomization?.result)}
            </div>
          </DropdownItem>

          <DropdownItem
            key="rollBackStatus"
            textValue="rollBackStatus"
            className={hidden(!canMoveToPrevStatus || !hasUpdatePermission)}
            onClick={() => updateStatus(caseId, prevStatus)}
          >
            Roll back to {getCaseStatusName(prevStatus, randomization?.result)}
          </DropdownItem>

          <DropdownItem
            key="declineCase"
            textValue="declineCase"
            className={hidden(!canDeclineCase || !hasUpdatePermission || isDirectAcquisitionCase)}
            onClick={declineCase.onOpen}
          >
            <span className="text-danger-30">Decline Case</span>
          </DropdownItem>

          <DropdownItem
            key="declineService"
            textValue="declineService"
            className={hidden(!canDeclineService || !hasUpdatePermission)}
            onClick={declineService.onOpen}
          >
            <span className="text-danger-30">Decline Service</span>
          </DropdownItem>

          <DropdownItem
            key="deleteDraft"
            textValue="deleteDraft"
            className={hidden(status !== CaseStatus.Draft || !canDeleteDraft)}
            onClick={onOpenDelete}
          >
            <span className="text-danger-30">Delete draft</span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {isOpenDelete && (
        <DeleteDraftModal caseId={caseId} deleteDraft={mutateDraftDelete} onOpenChange={onOpenChangeDelete} />
      )}

      {isOpenAttachments && (
        <CaseAttachmentsModal
          caseId={caseId}
          onOpenChange={onOpenChangeAttachments}
          links={links}
          files={files}
          disabled={!hasEditCaseDonorPermission}
          hintLabel="Uploaded by OPO"
          showRedactionInstructions
        />
      )}

      <CaseStatusChangeModals
        caseData={caseData}
        declineCase={declineCase}
        declineService={declineService}
        onOcs={onOcs}
        offOcs={offOcs}
      />
    </div>
  );
};
